import {
  batch,
  computed,
  signal,
  Signal,
  useSignalEffect,
} from '@preact/signals-react';
import {
  AiChatMessage,
  chatNow,
  RunEvent,
  RunResponse,
  RunResponseContent,
  SelectedAgent,
  ToolCall,
} from '@/features/ai-agents/shared/models';
import { routes } from '@/webapi/routes';
import { getQueryParam } from '@/utils/browser';
import { truncate } from '@/utils/types';
import { getJsonMarkdown } from '@/features/ai-agents/shared/message-format-processor';
import { createSignalsContext } from '@/signals/signal-context';
import {
  SessionListEntry,
  useAiMetadataApi,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { useAIResponseStream } from '@/features/ai-agents/shared/use-ai-response-stream';
import {
  RealtimeChatEventProcessor,
  shouldProcess,
  shouldReset,
} from '@/features/ai-agents/shared/event-processor';
import { useAIAuthHeaders } from '@/features/ai-agents/shared/use-ai-auth-headers';

export interface ChatSessionState {
  currentSessionId: Signal<string>;
  previousSessionId: Signal<string>;
  messages: Signal<AiChatMessage[]>;
  abortController: Signal<undefined | AbortController>;

  isProcessing: Signal<boolean>;
  isLoadingSession: Signal<boolean>;
  streamUrl?: Signal<string>;
  isLocalHost?: Signal<boolean>;

  processedEvents?: Signal<string[]>;
}

export interface ChatSessionActions extends ChatSessionContextProps {
  loadSession: () => Promise<void>;
  sendMessage: (msg: AiChatMessage) => Promise<void>;
  abortMessage: () => Promise<void>;
}

export interface ChatSessionContextProps {
  agent: SelectedAgent;
  initialSessionId?: string;
  userId?: string;
  onNewSession?: (session: SessionListEntry) => void;
  onNewMessageSent?: (userMessage: AiChatMessage) => void;
  onNewReply?: (
    agentMessage: AiChatMessage,
    userMessage: AiChatMessage,
    sessionId?: string,
  ) => void;
  onSessionLoaded?: (messages: AiChatMessage[]) => void;
  onLoadingChanged?: (loading: boolean) => void;
  eventProcessor?: RealtimeChatEventProcessor;
  sessionData?: Record<string, any>;
}

function createChatSessionState({
  agent,
  initialSessionId,
}: ChatSessionContextProps): ChatSessionState {
  const isProcessing = signal<boolean>(false);
  const isLoadingSession = signal<boolean>(false);
  const currentSessionId = signal<string>(initialSessionId ?? ``);
  const previousSessionId = signal<string>(``);

  const messages = signal<AiChatMessage[]>([]);
  const processedEvents = signal<string[]>([]);

  const streamUrl = computed(() => {
    if (!agent) return ``;
    return isLocalHost.value
      ? routes.convertToLocalAiUrl(routes.aiAgentRun(agent))
      : routes.aiAgentRun(agent);
  });

  const abortController = signal<AbortController | undefined>(undefined);

  const isLocalHost = signal(getQueryParam(`local-ai`) === `1`);

  return {
    currentSessionId,
    previousSessionId,
    isLoadingSession,
    isProcessing,
    messages,
    streamUrl,
    isLocalHost,
    abortController,
    processedEvents,
  };
}

function hookChatSessionState(
  state: ChatSessionState,
  props?: ChatSessionContextProps,
): ChatSessionActions {
  const { streamResponse } = useAIResponseStream();
  const { headers, user } = useAIAuthHeaders(state.isLocalHost.value);
  const {
    onNewReply,
    onNewMessageSent,
    onNewSession,
    onLoadingChanged,
    onSessionLoaded,
    userId,
    initialSessionId,
    agent,
    eventProcessor,
  } = props;
  const {
    isProcessing,
    isLoadingSession,
    currentSessionId,
    previousSessionId,
    messages,
    streamUrl,
    abortController,
    isLocalHost,
    processedEvents,
  } = state;

  const { getSession } = useAiMetadataApi(isLocalHost.value);

  useSignalEffect(() => onLoadingChanged?.(isProcessing.value));

  function generateClientDependencies(sentData: Record<string, string>) {
    const sessionData = {
      user_first_name: user?.store?.firstName || ``,
      user_last_name: user?.store?.lastName || ``,
      domain: user?.store?.domain || ``,
      ...(props?.sessionData || {}),
      ...(sentData || {}),
    };

    return JSON.stringify(sessionData);
  }

  const sendMessage = async (msg: AiChatMessage) => {
    try {
      processedEvents.value = [];
      isProcessing.value = true;
      const agentMsg = {
        role: `agent`,
        content: ``,
        tool_calls: [],
        streamingError: false,
        created_at: chatNow() + 1,
        images: msg?.images,
      } as AiChatMessage;

      messages.value = [...messages.value, msg, agentMsg];
      onNewMessageSent && onNewMessageSent(msg);

      let lastContent = ``;
      let newSessionId = currentSessionId.value;
      const formData = new FormData();
      formData.append(`message`, msg.content);
      formData.append(`stream`, `true`);
      formData.append(`session_id`, currentSessionId.value ?? ``);
      formData.append(`user_id`, userId);
      formData.append(
        `metadata`,
        JSON.stringify({
          user: user.store.email,
          alias: user.store.alias,
        }),
      );
      formData.append(`vsly_images`, JSON.stringify(msg.images || []));

      if (user?.store?.alias) {
        const clientDependencies = generateClientDependencies(msg.session_data);
        formData.append(`dependencies`, clientDependencies);
      }

      abortController.value = new AbortController();
      await streamResponse({
        headers,
        apiUrl: streamUrl.value,
        abortController: abortController.value,
        requestBody: formData,
        onChunk: (chunk: RunResponse) => {
          if (shouldProcess(eventProcessor, chunk)) {
            const processed = eventProcessor?.processEvent(agent, chunk);
            if (processed) {
              processedEvents.value = [...processedEvents.value, processed];
            }
          }

          if (shouldReset(eventProcessor, chunk)) {
            processedEvents.value = [];
          }

          if (
            agent.type === `team` &&
            (chunk.event === RunEvent.RunContent ||
              chunk.event === RunEvent.RunCompleted)
          ) {
            return;
          }

          if (
            chunk.event === RunEvent.RunStarted ||
            chunk.event === RunEvent.ReasoningStarted ||
            chunk.event === RunEvent.TeamReasoningStarted ||
            chunk.event === RunEvent.TeamRunStarted
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            newSessionId = chunk.session_id as string;
            if (newSessionId) {
              batch(() => {
                previousSessionId.value = newSessionId;
                currentSessionId.value = newSessionId;
              });
            }
            if (
              (!initialSessionId || initialSessionId !== chunk.session_id) &&
              chunk.session_id
            ) {
              onNewSession &&
                onNewSession({
                  session_id: chunk.session_id,
                  session_name: truncate(msg.content, 100),
                  title: truncate(msg.content, 100),
                  created_at: chunk.created_at ?? chatNow(),
                });
            }
          } else if (
            chunk.event === RunEvent.ToolCallStarted ||
            chunk.event === RunEvent.TeamToolCallStarted
          ) {
            const newMessages = [...messages.value];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === `agent`) {
              lastMessage.tool_calls = processChunkToolCalls(
                chunk,
                lastMessage.tool_calls,
              );
            }
            messages.value = newMessages;
          } else if (
            chunk.event === RunEvent.RunContent ||
            chunk.event === RunEvent.TeamRunContent
          ) {
            const newMessages = [...messages.value];
            const lastMessage = newMessages[newMessages.length - 1];
            if (
              lastMessage &&
              lastMessage.role === `agent` &&
              typeof chunk.content === `string`
            ) {
              const uniqueContent = chunk.content.replace(lastContent, ``);
              lastMessage.content += uniqueContent;
              lastContent = chunk.content;

              // Handle tool calls streaming
              lastMessage.tool_calls = processChunkToolCalls(
                chunk,
                lastMessage.tool_calls,
              );
              if (chunk.extra_data?.reasoning_steps) {
                lastMessage.extra_data = {
                  ...lastMessage.extra_data,
                  reasoning_steps: chunk.extra_data.reasoning_steps,
                };
              }

              if (chunk.extra_data?.references) {
                lastMessage.extra_data = {
                  ...lastMessage.extra_data,
                  references: chunk.extra_data.references,
                };
              }

              lastMessage.created_at =
                chunk.created_at ?? lastMessage.created_at;
              if (chunk.images) {
                lastMessage.images = chunk.images;
              }
              if (chunk.videos) {
                lastMessage.videos = chunk.videos;
              }
              if (chunk.audio) {
                lastMessage.audio = chunk.audio;
              }
            } else if (
              lastMessage &&
              lastMessage.role === `agent` &&
              !!chunk?.content &&
              typeof chunk?.content !== `string`
            ) {
              const jsonBlock = getJsonMarkdown(chunk?.content);

              lastMessage.content += jsonBlock;
              lastContent = jsonBlock;
            } else if (
              chunk.response_audio?.transcript &&
              typeof chunk.response_audio?.transcript === `string`
            ) {
              const { transcript } = chunk.response_audio;
              lastMessage.response_audio = {
                ...lastMessage.response_audio,
                transcript:
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  lastMessage.response_audio?.transcript + transcript,
              };
            }
            messages.value = newMessages;
          } else if (
            chunk.event === RunEvent.ReasoningCompleted ||
            chunk.event === RunEvent.TeamReasoningCompleted
          ) {
            const newMessages = [...messages.value];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === `agent`) {
              if (chunk.extra_data?.reasoning_steps) {
                lastMessage.extra_data = {
                  ...lastMessage.extra_data,
                  reasoning_steps: chunk.extra_data.reasoning_steps,
                };
              }
            }
            messages.value = newMessages;
          } else if (
            chunk.event === RunEvent.RunError ||
            chunk.event === RunEvent.TeamRunError
          ) {
            const errorContent = chunk.content as string;
            const newMessages = [...messages.value];
            const lastMessage = newMessages[newMessages.length - 1];
            if (
              lastMessage &&
              lastMessage.role === `agent` &&
              typeof chunk.content === `string`
            ) {
              lastMessage.streamingError = true;
              lastMessage.content = errorContent;
            }
            messages.value = newMessages;
          } else if (
            chunk.event === RunEvent.RunCompleted ||
            chunk.event === RunEvent.TeamRunCompleted
          ) {
            const prevMessages = messages.value;
            const newMessages = prevMessages.map((message, index) => {
              if (
                index === prevMessages.length - 1 &&
                message.role === `agent`
              ) {
                let updatedContent: string;
                if (typeof chunk.content === `string`) {
                  updatedContent = chunk.content;
                } else if (chunk?.content) {
                  try {
                    updatedContent = JSON.stringify(chunk.content);
                  } catch {
                    updatedContent = `Error parsing response`;
                  }
                }

                return {
                  ...message,
                  run_id: chunk.run_id,
                  content: updatedContent,
                  tool_calls: processChunkToolCalls(chunk, message.tool_calls),
                  images: chunk.images ?? message.images,
                  videos: chunk.videos ?? message.videos,
                  response_audio: chunk.response_audio,
                  created_at: chunk.created_at ?? message.created_at,
                  extra_data: {
                    reasoning_steps:
                      chunk.extra_data?.reasoning_steps ??
                      message.extra_data?.reasoning_steps,
                    references:
                      chunk.extra_data?.references ??
                      message.extra_data?.references,
                  },
                };
              }
              return message;
            });
            processedEvents.value = [];
            if (onNewReply) {
              onNewReply(
                newMessages?.[newMessages.length - 1],
                newMessages?.[newMessages.length - 2],
                currentSessionId.value,
              );
            }
            messages.value = newMessages;
          }
        },
        onError: (error: Error) => {
          processedEvents.value = [];
          console.error(`Error in AI response stream:`, error);
          isProcessing.value = false;
          abortController.value = undefined;
        },
        onComplete: () => {
          isProcessing.value = false;
          abortController.value = undefined;
          processedEvents.value = [];
        },
      });
    } catch (error) {
      console.error(`Error in AI response stream:`, error);
    } finally {
      isProcessing.value = false;
    }
  };

  const loadSession = async (): Promise<void> => {
    isLoadingSession.value = true;
    messages.value = await getSession(agent, currentSessionId?.value, userId);
    isLoadingSession.value = false;
    onSessionLoaded?.(messages.peek());
  };

  const abortMessage = async (): Promise<void> => {
    if (abortController?.peek()?.abort) {
      abortController.value.abort();
      messages.value = [...messages.value.slice(0, messages.value.length - 2)];
    }
  };

  return {
    loadSession,
    sendMessage,
    abortMessage,
    ...props,
  };
}

export const [ChatSessionContextProvider, _, useChatSession] =
  createSignalsContext(createChatSessionState, hookChatSessionState);

function processToolCall(toolCall: ToolCall, prevToolCalls: ToolCall[] = []) {
  const toolCallId =
    toolCall.tool_call_id || `${toolCall.tool_name}-${toolCall.created_at}`;

  const existingToolCallIndex = prevToolCalls.findIndex(
    (tc) =>
      (tc.tool_call_id && tc.tool_call_id === toolCall.tool_call_id) ||
      (!tc.tool_call_id &&
        toolCall.tool_name &&
        toolCall.created_at &&
        `${tc.tool_name}-${tc.created_at}` === toolCallId),
  );
  if (existingToolCallIndex >= 0) {
    const updatedToolCalls = [...prevToolCalls];
    updatedToolCalls[existingToolCallIndex] = {
      ...updatedToolCalls[existingToolCallIndex],
      ...toolCall,
    };
    return updatedToolCalls;
  }
  return [...prevToolCalls, toolCall];
}

function processChunkToolCalls(
  chunk: RunResponseContent | RunResponse,
  existingToolCalls: ToolCall[] = [],
) {
  let updatedToolCalls = [...existingToolCalls];
  // Handle new single tool object format
  if (chunk.tool) {
    updatedToolCalls = processToolCall(chunk.tool, updatedToolCalls);
  }
  // Handle legacy tools array format
  if (chunk.tools && chunk.tools.length > 0) {
    chunk?.tools.forEach((toolCall) => {
      updatedToolCalls = processToolCall(toolCall, updatedToolCalls);
    });
  }

  return updatedToolCalls;
}
